import React, { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import LayoutComp from "../../components/Layout/LayoutComp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import ZoneList from "./ZoneList";
import zoneServices from "../../services/zoneServices";
import useAuth from "../../Utils/useAuth";
import SearchInputComp from "../../components/SharedComp/SearchInputComp";
import { ErrorHandler, MainTitle } from "../../Utils/Constants.";
import { classify, DESIGN_TEXT, OUTCOME } from "../../Utils/e5Messages";
import saveZone from "./saveZone";
import useDebouncedCallback, { SEARCH_DEBOUNCE_MS } from "../../Utils/useDebouncedCallback";
import ConfirmDialog, { DEACTIVATE_COPY } from "../../components/SharedComp/ConfirmDialog";
import ZoneContext from "../../Utils/Context/ZoneContext";
import MessageComp from "../../components/SharedComp/MessageComp";
import { withRouter, Link, useHistory, useLocation } from "react-router-dom";
/**
//    * @component  Home page for Zone overview
//    * @description  Zone overview page where existing created zone list out and user can also add / edit particular zone
//    * @returns Zone overview Page UI
//    */
const Home = () => {
  // bearer token for authentication request
  const { bearerToken } = useAuth();
  const history = useHistory();
  const location = useLocation();

  // state maintain for zone overview
  const [open, setOpen] = useState(false); // to manage open and close modal
  const [isLoading, setIsLoading] = useState(false); // to manage loading state
  const [zoneList, setZoneList] = useState([]); // to set zone list item
  const [headerData, setHeaderData] = useState({}); // to set auth token in herder to fetch API
  const [showMsgComp, setShowMsgComp] = useState(false); // flag to show msg of success and error
  const [msgContent, setMsgContent] = useState({}); // to show msg content of success and error
  let [isNext, isNextFunc] = useState(true); // flag to manage upcoming list of zone
  let [errorThrow, isErrorThrow] = useState(false); // flag against catch error if zone list api throws error
  const [pageCount, setPageCount] = useState(1); // to set page count
  const [loaderSave, setLoaderSave] = useState(false); // to manage loader state which call on save zone
  const [timeZoneList, setTimeZoneList] = useState([]); // to set time zone list
  const [totalRecordCount, setTotalRecordCount] = useState(0); // total records count maintained
  const [zoneItem, setZoneItem] = useState({}); // to set edit item data
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoader, setDialogLoader] = useState(false);
  const [searchText, setSearchText] = useState(""); // current "Search Zone" text, kept across refreshes
  const [deactivateZone, setDeactivateZone] = useState(null); // zone in the Deactivate confirmation
  const [deactivateBusy, setDeactivateBusy] = useState(false);
  // only the latest list request may update the list (typing fast / toggling can overlap requests)
  const listSeq = useRef(0);
  // one "next page" request at a time for infinite scroll
  const loadingMore = useRef(false);

  /**
   * to hightLight fields when api trows error
   */
  const [hightLightFields, setHightLightFields] = useState({
    zoneName: false,
    zoneNumber: false,
  });

  // Request object for fetch zone list
  let request = {
    Pagination: {
      PageSize: 45,
      PageNumber: pageCount,
    },
    ZoneName: "",
  };

  // Request object for fetch zone list
  let timeZoneRequest = {
    DropdownKey: "Timezone",
  };

  // call once component render
  useEffect(() => {
    var data = JSON.parse(localStorage.getItem("userData"));

    let token =
      data !== null && data !== undefined
        ? data.authenticationToken.authToken
        : undefined;

    const header = {
      Authorization: `Bearer ${token ? token : bearerToken}`,
    };

    setHeaderData(header);
    // a toast handed over by another page (e.g. "Zone deactivated" from Zone Configuration)
    if (location.state && location.state.toast) {
      setShowMsgComp(true);
      setMsgContent(location.state.toast);
      history.replace({ ...location, state: {} });
    }
    fetchTimeZone(header);
    // Api calling function for zone list
    initialFetchZoneList(header);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   *
   * @param {*Bearer token for authentication} headerAuth
   */
  const fetchTimeZone = async (headerAuth) => {
    await zoneServices
      .getTimeZoneList(timeZoneRequest, headerAuth)
      .then((response) => {
        if (response && response.data) {
          if (response.data.httpStatusCode === 200) {
            setTimeZoneList(response.data.apiResponse);
          }
        } else if (response.error) {
          ErrorHandler(response.error);
          setTimeZoneList([]);
        }
      })
      .catch((e) => {
        ErrorHandler(e);
      });
  };
  /**
   *
   * @param {*Bearer token for authentication} headerAuth
   */
  const initialFetchZoneList = async (headerAuth) => {
    const seq = ++listSeq.current;
    setIsLoading(true);
    isErrorThrow(false);
    request.Pagination["PageNumber"] = 1;

    await zoneServices
      .getZoneList(request, headerAuth)
      .then((response) => {
        if (seq !== listSeq.current) return; // a newer list request is in charge
        if (response && response.data) {
          setTotalRecordCount(response.data.totalRecords);
          setZoneList(response.data.apiResponse);
          setIsLoading(false);
          isNextFunc(true);
        } else if (response.error) {
          setIsLoading(false);
          isErrorThrow(true);
        }
      })
      .catch((e) => {
        ErrorHandler(e);
        if (seq !== listSeq.current) return;
        setIsLoading(false);
        isErrorThrow(true);
      });
  };

  /**
   * @description call function on scroll event for fetch  zone list
   */
  const fetchMoreData = () => {
    isErrorThrow(false);

    if (zoneList.length >= totalRecordCount) {
      isNextFunc(false);
      return;
    }
    afterFetchMoreZoneList();
  };

  /**
   * @description function call on fetch more data or infinite scroll
   */

  const afterFetchMoreZoneList = async () => {
    if (loadingMore.current) return;
    loadingMore.current = true;
    const seq = listSeq.current;
    isErrorThrow(false);
    request.Pagination["PageNumber"] = pageCount + 1;
    // next page of the current search, not of the full list
    request["ZoneName"] = searchText;
    await zoneServices
      .getZoneList(request, headerData)
      .then((response) => {
        if (seq !== listSeq.current) return; // the list was reloaded meanwhile
        if (response && response.data) {
          if (response.data.apiResponse !== null) {
            let list = zoneList.concat(response.data.apiResponse);
            setZoneList(list);
            setPageCount(pageCount + 1);
          }
        } else if (response.error) {
          setIsLoading(false);
          isNextFunc(false);
        }
      })
      .catch((e) => {
        ErrorHandler(e);
        setIsLoading(false);
      })
      .finally(() => {
        loadingMore.current = false;
      });
  };

  /**
   * call for open dialog box of create/edit zone
   */
  const handleOpen = () => {
    setOpen(true);
    setDialogOpen(false);
  };

  // call for close dialog box of create/edit zone
  const handleClose = () => {
    setOpen(false);
    setDialogOpen(false);
  };

  /**
   * @description on change of search input value
   * @param {*onchange value} e
   */
  const runSearch = (zoneName) => {
    request["ZoneName"] = zoneName;
    // Api calling function for search list by zone name
    fetchSearchList();
    setPageCount(1);
  };
  // search waits until typing pauses (one request instead of one per keystroke)
  const [debouncedSearch, cancelSearch] = useDebouncedCallback(runSearch, SEARCH_DEBOUNCE_MS);

  const handleChange = (e) => {
    let zoneName = e.target.value;
    setSearchText(zoneName);
    if (zoneName !== "" && zoneName.length > 0) {
      debouncedSearch(zoneName);
    } else if (zoneName === "") {
      // cleared: show the full list straight away
      cancelSearch();
      setPageCount(1);
      initialFetchZoneList(headerData);
    }
  };

  /**
   *  @description call function on search by zone name
   * @param {*request payload of zone name} request
   */
  const fetchSearchList = async () => {
    const seq = ++listSeq.current;
    request.Pagination["PageNumber"] = 1;
    setIsLoading(true);
    isErrorThrow(false);
    await zoneServices
      .getZoneList(request, headerData)
      .then((response) => {
        if (seq !== listSeq.current) return; // a newer list request is in charge
        if (response && response.data) {
          isErrorThrow(false);
          setTotalRecordCount(response.data.totalRecords);
          setZoneList(response.data.apiResponse);
          setIsLoading(false);
          isNextFunc(true);
        } else if (response.error) {
          setIsLoading(false);
          isErrorThrow(true);
        }
      })
      .catch((e) => {
        ErrorHandler(e);
        if (seq !== listSeq.current) return;
        setIsLoading(false);
        isErrorThrow(true);
      });
  };

  //
  /**
   * @description function call to set edit item data on modal open
   * @param {*data of zone} item
   */
  const clearAndSetItem = (item) => {
    setZoneItem(item);
    setHightLightFields({ zoneName: false, zoneNumber: false });
  };

  const onClickClose = () => {
    setDialogOpen(true);
    // props.onModalClose();
  };
  const confirmModalClose = () => {
    setDialogOpen(false);
    // props.onModalClose();
  };
  /**
   * @description submit function for create and edit zone with same API
   * @param {*form submitted values, already trimmed by the form} values
   * @param {*on edit form user id send} id
   * @returns {ok, fieldErrors, formError} for the form to show (E5 design)
   */
  const handleSubmit = async (values, id) => {
    if (dialogOpen === true) {
      setDialogLoader(true);
    } else {
      setLoaderSave(true);
    }
    setPageCount(1);
    const { feedback, response } = await saveZone(values, id, headerData);
    setLoaderSave(false);
    setDialogLoader(false);
    setDialogOpen(false);

    if (feedback.ok) {
      handleClose();
      clearAndSetItem({});
      initialFetchZoneList(headerData);
      setShowMsgComp(true);
      setMsgContent({
        msg: id ? DESIGN_TEXT.zoneUpdated : DESIGN_TEXT.zoneCreated,
        type: "success",
      });
    } else if (feedback.toast) {
      ErrorHandler(response);
      setShowMsgComp(true);
      setMsgContent({ msg: feedback.toast, type: "error" });
    }
    return feedback;
  };
  /**
   * @description reload the list after a change, keeping the current search text
   */
  const refreshZoneList = () => {
    setPageCount(1);
    if (searchText) {
      request["ZoneName"] = searchText;
      fetchSearchList();
    } else {
      initialFetchZoneList(headerData);
    }
  };

  const showToast = (msg, type) => {
    setShowMsgComp(true);
    setMsgContent({ msg, type });
  };

  /**
   * @description zone card ⋯ → Deactivate: open the confirmation
   */
  const onDeactivateZone = (zone) => {
    setDeactivateZone(zone);
  };

  /**
   * @description confirm Deactivate (E5 design): success → toast + refresh;
   * already deactivated / not found → toast + refresh (someone else changed it);
   * anything else → dialog closes, error toast, the zone stays listed
   */
  const confirmDeactivateZone = async () => {
    const zone = deactivateZone;
    setDeactivateBusy(true);
    const response = await zoneServices.DeactivateZone(zone.id, headerData);
    const result = classify(response);
    setDeactivateBusy(false);
    setDeactivateZone(null);

    if (result.outcome === OUTCOME.SUCCESS) {
      showToast(DESIGN_TEXT.zoneDeactivated, "success");
      refreshZoneList();
    } else if (
      result.outcome === OUTCOME.ALREADY_IN_STATE ||
      result.outcome === OUTCOME.NOT_FOUND
    ) {
      showToast(result.text, "error");
      refreshZoneList();
    } else {
      ErrorHandler(response);
      showToast(
        result.outcome === OUTCOME.AUTH ? result.text : DESIGN_TEXT.generic,
        "error"
      );
    }
  };

  return (
    <LayoutComp>
      <>
        <MessageComp msg={msgContent} id="login-success" show={showMsgComp} />
        <Box className="zoneBox">
          {/* E5 design header: Add Zone + tile, then Zone Overview with the search on the right */}
          <Box sx={{ mb: "14px" }}>
            <MainTitle title="Add Zone" />
          </Box>
          <Link
            onClick={handleOpen}
            to={{
              state: {
                zoneDetails: {},
              },
            }}
            style={{ display: "inline-block", marginBottom: 24 }}
          >
            <Box
              component="button"
              type="button"
              title="Add Zone"
              aria-label="Add Zone"
              sx={{
                width: 56,
                height: 56,
                borderRadius: "12px",
                border: "1px solid #e3e3e8",
                backgroundColor: "#fff",
                boxShadow: "0 2px 6px rgba(16,24,40,.12)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#864797",
                fontSize: 20,
                "&:hover": { backgroundColor: "#f9f4fb", borderColor: "#864797" },
              }}
            >
              <FontAwesomeIcon icon={faPlus} />
            </Box>
          </Link>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              flexWrap: "wrap",
              mb: "24px",
            }}
          >
            <MainTitle title="Zone Overview" />
            <SearchInputComp handleChange={handleChange} placeholderText={"Search Zone"} />
          </Box>

          <ZoneContext.Provider
            value={{
              open,
              zoneList,
              handleClose,
              handleOpen,
              isLoading,
              loaderSave,
              errorThrow,
              headerData,
              handleSubmit,
              timeZoneList,
              totalRecordCount,
              hightLightFields,
              clearAndSetItem,
              zoneItem,
              dialogOpen,
              onClickClose,
              confirmModalClose,
              dialogLoader,
              onDeactivateZone,
              searchText,
            }}
          >
            <ZoneList fetchMoreData={fetchMoreData} isNext={isNext} />
          </ZoneContext.Provider>
          <ConfirmDialog
            open={!!deactivateZone}
            title={DEACTIVATE_COPY.zone.title}
            label={deactivateZone ? DEACTIVATE_COPY.zone.label(deactivateZone) : ""}
            bullets={DEACTIVATE_COPY.zone.bullets}
            busy={deactivateBusy}
            onConfirm={confirmDeactivateZone}
            onCancel={() => setDeactivateZone(null)}
          />
        </Box>
      </>
    </LayoutComp>
  );
};

export default withRouter(Home);
